import { trigger, transition, style, animate } from "@angular/animations";
import { Component, inject, Input, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren } from "@angular/core";
import { FormGroup, FormControl, Validators } from "@angular/forms";
import { MatPaginator } from "@angular/material/paginator";
import { MatSort } from "@angular/material/sort";
import { MatTableDataSource } from "@angular/material/table";
import { SharedService } from "@shared/services/shared.service";
import { AddressComponent } from "app/components/reusable/address/address.component";
import { DocumentComponent } from "app/components/reusable/document/document.component";
import { IOnCampusBuilding } from "app/models/licenses/hospital/on-campus-building";
import { AddressService } from "app/services/address/address.service";
import { DocumentService } from "app/services/document/document.service";
import { OnCampusBuildingService } from "app/services/license/hospital/on-campus-building.service";
import { firstValueFrom } from "rxjs";

@Component({
  selector: 'on-campus-building',
  templateUrl: './on-campus-building.component.html',
  styleUrls: ['./on-campus-building.component.scss'],
    animations: [
      trigger('slideRight', [
        transition(':enter', [
          style({ transform: 'translateX(100%)' }),
          animate('200ms ease-in', style({ transform: 'translateX(0%)' }))
        ]),
        transition(':leave', [
          animate('200ms ease-in', style({ transform: 'translateX(100%)' }))
        ])
      ])
    ]
})

export class OnCampusBuildingComponent implements OnInit, OnDestroy {
  @ViewChildren(DocumentComponent) documents!: QueryList<DocumentComponent>;
  @ViewChildren(AddressComponent) addresses!: QueryList<AddressComponent>;
  @Input() entityId: number = 0;
  @Input() entityType: string = '';
  @Input() isChange: boolean = false;
  @Input() canEdit: boolean = false;
  @Input() HospitalLicenseId: string = "";

  public entity: string = 'OnCampusBuilding';
  public addingBuilding: boolean = false;
  public deletingBuilding: boolean = false;
  public managingBuilding: boolean = false;
  public isOutOfState: boolean = false;
  public newBuildingCreated: boolean = false;
  public includeArchived: boolean = false;
  public onCampusBuildingTypes: string[] = ['Other'];

  public onCampusBuilding: IOnCampusBuilding = {
    id: 0,
    buildingId: "",
    buildingName: "",
    parentId: 0,
    archived: false,
    addresses: [],
    documents: []
  }
  public buildings: IOnCampusBuilding[] = [];
  public buildingForm = new FormGroup({
    buildingId: new FormControl('', Validators.required),
    buildingName: new FormControl('', Validators.required)
  });
  public onCampusBuildings: IOnCampusBuilding[] = [];


  public onCampusBuildingColumns: string [] = ['buildingId', 'buildingName', 'archived'];
  public onCampusBuildingSource = new MatTableDataSource<IOnCampusBuilding>(this.onCampusBuildings);
  @ViewChild('onCampusBuildingPaginator', {static: false}) onCampusBuildingPaginator!: MatPaginator;
  @ViewChild('onCampusBuildingSort', {static: false}) onCampusBuildingSort!: MatSort;

  public sharedService = inject(SharedService);
  public buildingService = inject(OnCampusBuildingService);
  public addressService = inject(AddressService);
  public documentService = inject(DocumentService);

  ngOnInit(): void {
    this.getBuildings(this.includeArchived);
  }

  ngOnDestroy(): void {
    if(this.managingBuilding){
      this.updateBuilding();
    }
  }

  getBuildings(includeArchived: boolean): void {
    this.includeArchived = includeArchived;

    this.buildingService.getBuildings(this.entityId, this.entityType, this.includeArchived).subscribe({
      next: response => this.buildings = response,
      complete: () => this.updateBuildingTable()
    });
  }

  updateBuildingTable(): void {
    setTimeout(() => {
      this.onCampusBuildingSource.data = this.buildings;
      this.onCampusBuildingSource.paginator = this.onCampusBuildingPaginator;
      this.onCampusBuildingSource.sort = this.onCampusBuildingSort;
    }, 100);
  }

  selectBuilding(building: IOnCampusBuilding): void {
    this.deletingBuilding = false;
    this.onCampusBuilding = building;
    this.managingBuilding = true;
    this.toggleBuildingForm(true);
    this.updateBuildingForm(building);

  }

  toggleBuildingForm(adding: boolean): void{
    this.managingBuilding = adding;
    this.updateBuildingForm(this.getEmptyBuilding());
    if(!adding){
      this.updateBuildingTable();
    }
  }

   getEmptyBuilding(): IOnCampusBuilding {
    return {
      id: 0,
      buildingId: "",
      buildingName: "",
      parentId: 0,
      archived: false,
      addresses: [],
      documents: []
    }
   }

   updateBuildingForm(building: IOnCampusBuilding): void {
     this.buildingForm.reset({
       buildingId: building.buildingId,
       buildingName: building.buildingName
     });
   }

   updateBuilding(): void {
     let form = this.buildingForm.value;
     this.onCampusBuilding.buildingId = form.buildingId ?? '';
     this.onCampusBuilding.buildingName = form.buildingName ?? '';
   }

   createBuilding(): void {
    this.onCampusBuilding = this.getEmptyBuilding();
    this.addingBuilding = true;
    this.onCampusBuilding.parentId = this.entityId;
    this.managingBuilding = true;
     this.buildingService.createBuilding(this.onCampusBuilding, this.entityId, this.entityType).subscribe({
       next: response => {
         this.buildings.push(response);
         this.onCampusBuilding = response;
       },
       error: e => {
         this.sharedService.toastr.error("Error Creating On Campus Building");
         console.log('error creating On Campus Building', e);
       },
       complete: () => {
          this.toggleBuildingForm(true);
          this.newBuildingCreated = true;
          this.deletingBuilding = false;
       }
     });
   }

   saveBuilding(): void {
     this.updateBuilding();
     this.buildingService.updateOnCampusBuilding(this.onCampusBuilding).subscribe({
       complete: () => {
         this.newBuildingCreated = false;
         this.toggleBuildingForm(false);
       },
       error: e => {
         this.sharedService.toastr.error("Could Not Save On Campus Building");
         console.log('error updating on campus building', e);
       }
     });
   }

   archiveBuilding(building: IOnCampusBuilding, archive: boolean): void {
     let archiving: string = archive? 'Archive ' : 'Un-Archive ';
     this.sharedService.openConfirm(archiving + 'this building?');
     this.sharedService.confirmed().subscribe(
       confirmed => {
         if(confirmed){
           this.buildingService.archiveOnCampusBuilding(building.id, archive).subscribe({
             complete: () => {
              building.archived = archive;
               if(archive){
                 this.buildings = this.buildings.filter(o => o.id !== building.id);
                 this.onCampusBuildingSource.data = this.buildings;
                 this.onCampusBuildingSource.paginator = this.onCampusBuildingPaginator;
                 this.onCampusBuildingSource.sort = this.onCampusBuildingSort;
               }
               this.toggleBuildingForm(false);
             },
             error: e => {
               this.sharedService.toastr.error("Could Not Archive Building");
               console.log('error archiving building', e);
             }
           });
         }
       }
     );
   }

   confirmDelete(buildingId: number): void {
     this.sharedService.openConfirm('Delete on campus building ' + this.onCampusBuilding.buildingName + ' ?');
     this.sharedService.confirmed().subscribe(
       confirmed => {
         if(confirmed){
           this.deletingBuilding = true;
           this.deleteBuilding(buildingId);
         }
       }
     );
   }

  deleteBuilding(buildingId: number): void {
    this.buildingService.deleteOnCampusBuilding(buildingId).subscribe({
      complete: () => {
        this.newBuildingCreated = false;
        this.buildings = this.buildings.filter(a => a.id !== buildingId);
        this.onCampusBuildingSource.data = this.buildings;
        this.onCampusBuildingSource.paginator = this.onCampusBuildingPaginator;
        this.onCampusBuildingSource.sort = this.onCampusBuildingSort;
        this.toggleBuildingForm(false);
      },
      error: e => console.log('error deleting owner', e)
    });
  }

  async validateBuildingAddresses(): Promise<string[]> {
    let errors: string[] = [];

    for (const building of this.buildings) {
      try {
        const response = await firstValueFrom(
          this.addressService.getAddresses(building.id, 'OnCampusBuilding', false)
        );
        building.addresses = response;

        if (!building.addresses.find(a =>
          !a.archived && a.addressTypes.find(t => t.type === 'Physical')
        )) {
          errors.push(`Building ${building.buildingName} must have a physical address`);
        }
      } catch (e) {
        console.log('Get address error', e);
      }
    }

    return errors;
  }
}
